import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Text } from 'ds/components/typography';
import PlusIcon from 'assets/svg/plus-ic.svg';
import MinusIcon from 'assets/svg/minus-ic.svg';
import { useSendEvent } from 'analytics/useSendEvent';

const Item = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2, 0)};
  background: transparent;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

const Icon = styled.span`
  width: 24px;
  flex: 0 0 24px;
  text-align: left;
  font-size: 24px;
  line-height: 1;
  user-select: none;
`;

const Body = styled.div<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${({ isOpen }) => (isOpen ? '1fr' : '0fr')};
  transition: grid-template-rows 250ms ease;
`;

const BodyInner = styled.div`
  overflow: hidden;
`;

const Question = styled(Text.withComponent('h3'))`
  text-align: right;
`;

const Answer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing(2)};
`;

export interface AccordionItem {
  id: string;
  faqId: string;
  question: React.ReactNode;
  answer: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  initialActiveId?: string | null;
}

export const Accordion: React.FC<AccordionProps> = ({ items, initialActiveId = null }) => {
  const [ activeId, setActiveId ] = useState<string | null>(initialActiveId);
  const sendEvent = useSendEvent();

  const handleClick = (item: AccordionItem, index: number) => {
    const isCurrentlyOpen = activeId === item.id;
    setActiveId(isCurrentlyOpen ? null : item.id);
    sendEvent('madad_faq_interaction', 'madad', {
      event: {
        action_type: isCurrentlyOpen ? 'collapse' : 'expand',
        faq_id: item.faqId,
        faq_position: index + 1,
      },
    });
  };

  return (
    <div>
      {items.map((item, index) => {
        const isOpen = activeId === item.id;

        return (
          <Item key={item.id}>
            <Header type="button" onClick={() => handleClick(item, index)}>
              <Question weight="bold">{item.question}</Question>
              <Icon aria-hidden>{isOpen ? <MinusIcon /> : <PlusIcon />}</Icon>
            </Header>
            <Body isOpen={isOpen}>
              <BodyInner>
                <Answer>
                  <Text>{item.answer}</Text>
                </Answer>
              </BodyInner>
            </Body>
          </Item>
        );
      })}
    </div>
  );
};

